import { Modal, TextInput, Textarea, Button, Stack, Group, Text, Alert } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState } from 'react';
import { IconCheck, IconMail, IconUser, IconBuilding, IconPhone, IconAlertCircle } from '@tabler/icons-react';

interface ContactFormModalProps {
  opened: boolean;
  onClose: () => void;
}

interface ContactFormValues {
  name: string;
  email: string;
  organization: string;
  phone: string;
  message: string;
}

export function ContactFormModal({ opened, onClose }: ContactFormModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<ContactFormValues>({
    initialValues: {
      name: '',
      email: '',
      organization: '',
      phone: '',
      message: '',
    },
    validate: {
      name: (value) => (value.length < 2 ? 'Name must be at least 2 characters' : null),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      organization: (value) =>
        value.length < 2 ? 'Organization name must be at least 2 characters' : null,
      phone: (value) =>
        value.length < 10 ? 'Phone number must be at least 10 digits' : null,
    },
  });

  const handleSubmit = async (values: ContactFormValues) => {
    setLoading(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${apiUrl}/demo-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle validation errors
        if (data.details) {
          const errorMessages = Object.values(data.details)
            .flat()
            .join(', ');
          throw new Error(errorMessages);
        }
        throw new Error(data.error || 'Failed to submit demo request');
      }

      // Success!
      setSubmitted(true);

      // Reset form and close modal after delay
      setTimeout(() => {
        setSubmitted(false);
        setError(null);
        form.reset();
        onClose();
      }, 3000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!submitted && !loading) {
      form.reset();
      setError(null);
    }
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text size="xl" fw={700}>
          Request a Demo
        </Text>
      }
      size="lg"
      centered
    >
      {submitted ? (
        <Stack align="center" py="xl">
          <IconCheck size={64} color="#4a8c71" />
          <Text size="lg" fw={600} ta="center">
            Thank you for your interest!
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            We'll get back to you within 24 hours to schedule your personalized demo.
          </Text>
        </Stack>
      ) : (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Fill out the form below and we'll reach out to schedule a personalized demo
              and discuss how LivingSyncs can help your organization.
            </Text>

            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                title="Submission Error"
                onClose={() => setError(null)}
                withCloseButton
              >
                {error}
              </Alert>
            )}

            <TextInput
              label="Full Name"
              placeholder="John Doe"
              leftSection={<IconUser size={16} />}
              required
              {...form.getInputProps('name')}
            />

            <TextInput
              label="Email Address"
              placeholder="john@example.com"
              leftSection={<IconMail size={16} />}
              required
              {...form.getInputProps('email')}
            />

            <TextInput
              label="Organization Name"
              placeholder="Your Facility or Group Home"
              leftSection={<IconBuilding size={16} />}
              required
              {...form.getInputProps('organization')}
            />

            <TextInput
              label="Phone Number"
              placeholder="(555) 123-4567"
              leftSection={<IconPhone size={16} />}
              required
              {...form.getInputProps('phone')}
            />

            <Textarea
              label="Tell us about your needs"
              placeholder="How many facilities do you manage? What are your biggest challenges?"
              minRows={4}
              {...form.getInputProps('message')}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={loading}
                disabled={loading}
                style={{
                  background: 'linear-gradient(135deg, #4a8c71 0%, #356651 100%)',
                }}
              >
                {loading ? 'Submitting...' : 'Request Demo'}
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
